import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import SEO from "@/components/SEO";
import { Link, useLocation } from "react-router-dom";
import Layout from "@/components/Layout";
import PageHero from "@/components/PageHero";
import servicesHero from "@/assets/services-hero.jpg";
import { practiceAreas } from "@/data/practiceAreas";
import { attorneys } from "@/data/attorneys";
import { ChevronDown, ArrowRight, Users } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

const ServicesPage = () => {
  const location = useLocation();
  const hashSlug = location.hash ? location.hash.slice(1) : "";
  const [openItem, setOpenItem] = useState<string | undefined>(hashSlug || undefined);
  const itemRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    if (hashSlug) {
      setOpenItem(hashSlug);
      // Delay scroll to allow accordion to render
      setTimeout(() => {
        const el = itemRefs.current[hashSlug];
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 300);
    }
  }, [hashSlug]);

  return (
    <Layout>
      <SEO title="Practice Areas & Services | Vaughan McLean" description="Explore Vaughan McLean's comprehensive legal services including commercial litigation, insurance coverage, employment law, transportation, and more." />
      <PageHero backgroundImage={servicesHero} title="Services" subtitle="Practices & Industries" />

      <section className="section-dark py-24">
        <div className="container mx-auto px-8 lg:px-16">
          <div className="flex flex-col lg:flex-row gap-16 mb-16">
            <div className="lg:w-1/3">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <p className="text-xs tracking-[0.3em] uppercase font-body text-primary mb-4">
                  Practices & Industries
                </p>
                <h2 className="font-heading text-4xl font-light text-foreground mb-6">
                  Comprehensive Legal Solutions
                </h2>
                <p className="text-muted-foreground font-body leading-relaxed text-sm">
                  We offer a full spectrum of legal services to meet the diverse needs of our
                  clients. Our practice groups work collaboratively to deliver integrated solutions.
                  Select a practice area below to learn more about our capabilities and the attorneys
                  who can assist you.
                </p>
              </motion.div>
            </div>

            <div className="lg:w-2/3">
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="text-muted-foreground font-body leading-relaxed text-sm"
              >
                Click on any practice area to view a detailed description. Where applicable, you can
                also expand the list of experienced attorneys associated with that practice to find
                the right counsel for your needs.
              </motion.p>
            </div>
          </div>

          <Accordion type="single" collapsible className="w-full" value={openItem} onValueChange={setOpenItem}>
            {practiceAreas.map((area, i) => (
              <motion.div
                key={area.slug}
                ref={(el) => { itemRefs.current[area.slug] = el; }}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: i * 0.02 }}
                viewport={{ once: true }}
              >
                <AccordionItem value={area.slug} className="border-border">
                  <AccordionTrigger className="py-5 px-4 text-sm font-body text-muted-foreground hover:text-primary hover:bg-card transition-all duration-200 hover:no-underline">
                    <span className="text-left">{area.name}</span>
                  </AccordionTrigger>
                  <AccordionContent className="px-4">
                    <div className="py-4 space-y-4">
                      {area.description.map((para, idx) => (
                        <p key={idx} className="text-sm font-body text-muted-foreground leading-relaxed">
                          {para}
                        </p>
                      ))}

                      <PracticeAttorneys slugs={area.attorneySlugs} />
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </motion.div>
            ))}
          </Accordion>
        </div>
      </section>
    </Layout>
  );
};

function PracticeAttorneys({ slugs }: { slugs: string[] }) {
  const [open, setOpen] = useState(false);

  const matched = slugs
    .map((slug) => attorneys.find((a) => a.slug === slug))
    .filter(Boolean) as typeof attorneys;

  if (matched.length === 0) return null;

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="mt-6">
      <CollapsibleTrigger className="flex items-center gap-2 text-xs tracking-[0.15em] uppercase font-body text-primary hover:text-primary/80 transition-colors cursor-pointer">
        <Users className="w-3.5 h-3.5" />
        <span>{matched.length} Practicing Attorney{matched.length !== 1 ? "s" : ""}</span>
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-4">
        <div className="grid grid-cols-1 gap-5">
          {matched.map((attorney) => (
            <Link
              key={attorney.slug}
              to={`/team/${attorney.slug}`}
              className="group flex items-center gap-6 p-6 border border-border rounded hover:border-primary hover:bg-card transition-all duration-200"
            >
              {attorney.photo ? (
                <div className="w-24 h-24 rounded-full overflow-hidden shrink-0">
                  <img
                    src={attorney.photo}
                    alt={attorney.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              ) : (
                <div className="w-24 h-24 rounded-full border border-border flex items-center justify-center shrink-0 group-hover:border-primary transition-colors">
                  <span className="font-heading text-lg text-primary">{attorney.initials}</span>
                </div>
              )}
              <div className="min-w-0">
                <p className="text-lg font-body text-foreground truncate group-hover:text-primary transition-colors">
                  {attorney.name}
                </p>
                {attorney.title && (
                  <p className="text-base font-body text-muted-foreground truncate">{attorney.title}</p>
                )}
              </div>
              <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary ml-auto shrink-0 transition-colors" />
            </Link>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export default ServicesPage;
